/**
 * Pointing at a scanned copy.
 *
 * Where no transcription of a work exists, a scan is often the only thing
 * available — and it is EVIDENCE rather than text. It cannot be searched, cited
 * into a sentence, set in type, or checked for a letter a vocalizer quietly
 * changed, which is every job the library exists to do.
 *
 * So a scan is pointed at, never held. What the library stores is the passage
 * somebody transcribed and the page it came from; the scan is how a reader
 * confirms the transcription says what it claims. That also keeps the copying
 * to the passages actually quoted, which is what a citation is, rather than
 * mirroring somebody else's whole book.
 */

/** How to build a page address at each site we point at. */
const SITES: Record<string, (ref: string, page?: number) => string> = {
  /**
   * HebrewBooks opens a book at a page through its viewer, so a citation can
   * land on the page it quotes rather than at the front of the volume.
   */
  hebrewbooks: (ref, page) =>
    `https://beta.hebrewbooks.org/pdfpager.aspx?req=${encodeURIComponent(ref)}${
      page ? `&pgnum=${page}` : ''
    }`,
  /** Sefaria addresses a text by reference rather than by page. */
  sefaria: (ref) => `https://www.sefaria.org/${encodeURIComponent(ref)}`,

  /**
   * A manuscript library, addressed by shelfmark.
   *
   * It offers no page parameter, so a citation opens the volume rather than the
   * leaf. Recorded anyway: a manuscript is the cleanest chain of custody a text
   * can have, and pointing at the wrong page of the right manuscript is a
   * different order of problem from pointing at nothing.
   *
   * The shelfmark is hyphenated and lives under `/items/`. An underscored form
   * redirects here, which is not a reason to store it: a redirect is somebody
   * else's courtesy and the canonical address is the one that survives them
   * tidying up.
   */
  etshaim: (ref) =>
    `https://etshaimmanuscripts.nl/items/${encodeURIComponent(ref)}/`,

  /**
   * The Internet Archive, addressed by item identifier.
   *
   * A page parameter exists in its reader, but the identifier alone is what
   * survives: several of these items are lending-restricted, and a deep link
   * into a book somebody must borrow first opens a page that is not there.
   *
   * Nothing here is held as text, and that is a finding rather than an
   * omission. The unrestricted items DO carry an OCR layer, and it is unusable
   * for this material: one reads the Hebrew as garbled Latin and holds not a
   * single Hebrew letter, the other holds Hebrew letters that are noise. An OCR
   * file is not a transcription, and a corpus whose claim is that the letters
   * shown are the letters quoted cannot take either.
   */
  archive: (ref) => `https://archive.org/details/${encodeURIComponent(ref)}`,
}

/**
 * The address of the page a passage was transcribed from.
 *
 * Null where there is nothing to point at, so a caller renders no link rather
 * than a dead one — a citation offering a link that goes nowhere is worse than
 * a citation that offers none.
 */
export function scanUrl(input: {
  scanSite: string | null
  scanRef: string | null
  page?: number | null
}): string | null {
  if (!input.scanSite || !input.scanRef) return null
  const build = SITES[input.scanSite]
  if (!build) return null
  return build(input.scanRef, input.page ?? undefined)
}

/** The sites a scan reference may name. */
export const scanSites = Object.keys(SITES)
